import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import type { User, UserManager } from "oidc-client-ts";
import rbacModule, { type RbacAbility } from "@shipbob/rbac";
// Relative (not @/) imports: auth.store is MF-exposed as ./authStore, and its
// emitted d.ts ships to consumers where the producer's @/ alias can't resolve.
import { getUserManager } from "./user-manager";
import { fetchUserPermissions } from "./permissions";
import { useUserStore } from "../user/user.store";

// @shipbob/rbac is a UMD/CJS bundle whose exports object carries the factory
// under `default`; depending on the bundler's interop we receive either the
// factory itself or that namespace object. Unwrap both shapes.
const buildAbilityForUser =
  typeof rbacModule === "function"
    ? rbacModule
    : (rbacModule as unknown as { default: typeof rbacModule }).default;

/**
 * Permission ids from the OneID `Perm` claim, when the token carries them
 * (JWT-token clients, e.g. WMS/ToolBob-style hosts). oidc-client-ts surfaces
 * multi-value claims as arrays and single-value claims as plain strings.
 * Reference-token clients (like ShipBob.OneId.User.Clone) have NO Perm claim
 * client-side, those fall back to the user-management API.
 */
function claimPermissions(user: User): string[] {
  const perm = (user.profile as Record<string, unknown>).Perm;
  if (Array.isArray(perm)) {
    return perm.filter((p): p is string => typeof p === "string");
  }
  return typeof perm === "string" ? [perm] : [];
}

/**
 * Auth Store - Manages authentication state and operations
 *
 * Uses oidc-client-ts directly for OIDC (PKCE) auth against the ShipBob identity server.
 *
 * Usage:
 * ```typescript
 * import { useAuthStore } from '@/shared/auth/auth.store'
 * // or from MFE:
 * import { useAuthStore } from 'shipbob_merchant_app_host/authStore'
 *
 * const authStore = useAuthStore()
 * console.log(authStore.isAuthenticated)
 * await authStore.login()
 * const token = await authStore.getAccessToken()
 * ```
 */
export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isInitialized = ref(false);

  // RBAC: authorization is initialized with authentication (host contract -
  // rbac state is part of the ./authStore expose). Loaded by loadRbac() on
  // the initialize/handleCallback paths, the router guard awaits initialize()
  // before any requiresAuth route renders, so the ability is ready alongside
  // the session. Cleared on logout/expiry by the watch below.
  const ability = ref<RbacAbility>();
  let abilityUserSub: string | undefined;

  watch(
    user,
    (currentUser) => {
      if (!currentUser || currentUser.expired) {
        ability.value = undefined;
        abilityUserSub = undefined;
      }
    },
    { flush: "sync" },
  );

  async function loadRbac(currentUser: User) {
    // Same user (token refresh, re-init), keep the existing ability.
    if (ability.value && abilityUserSub === currentUser.profile.sub) {
      return;
    }

    // Fast path: Perm claim in the token. Fallback: user-management API
    // (MerchantApp pattern, reference-token clients).
    let permissions = claimPermissions(currentUser);
    if (permissions.length === 0) {
      try {
        permissions = await fetchUserPermissions();
      } catch (err) {
        console.error("[Auth] Permissions fetch failed, ability is deny-all:", err);
        permissions = [];
      }
    }

    ability.value = buildAbilityForUser(permissions);
    abilityUserSub = currentUser.profile.sub;
  }

  function can(action: string, subject: string): boolean {
    return ability.value?.can(action, subject) ?? false;
  }

  // Getters
  const isAuthenticated = computed(() => {
    return !!user.value && !user.value.expired;
  });

  const accessToken = computed(() => {
    return user.value?.access_token || null;
  });

  const userProfile = computed(() => {
    return user.value?.profile || null;
  });

  /**
   * The UserManager with this store's event subscriptions attached.
   *
   * Token renewal is owned by oidc-client-ts (`automaticSilentRenew: true` in
   * user-manager.ts): it listens for accessTokenExpiring and runs ONE
   * signinSilent() per expiry. This store does not start a second one. The
   * renewed User surfaces through `userLoaded` (also raised after callback and
   * manual signinSilent), so that is the hook that keeps `user`, and with it
   * the token the API client reads, current.
   */
  // Per store instance, not per UserManager: the manager is a module singleton
  // but a store can be re-created (standalone harness, tests), and the new
  // instance must receive the events, not the old closure.
  let subscribed = false;
  function getAuthService(): UserManager {
    const userManager = getUserManager();
    if (!subscribed) {
      subscribed = true;
      userManager.events.addUserLoaded((loadedUser) => {
        user.value = loadedUser;
      });
      userManager.events.addSilentRenewError((err: unknown) => {
        console.error("[Auth] Silent renew error:", err);
      });
    }
    return userManager;
  }

  // Actions
  async function initialize() {
    if (isInitialized.value) return;

    isLoading.value = true;
    try {
      const service = getAuthService();
      user.value = await service.getUser();
      if (user.value && !user.value.expired) {
        await loadRbac(user.value);
      }
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Initialization failed:", err);
    } finally {
      isInitialized.value = true;
      isLoading.value = false;
    }
  }

  /**
   * Starts the OIDC redirect. `returnTo` (an in-app path) rides through the
   * round trip in the OIDC `state` and comes back from handleCallback().
   */
  async function login(returnTo?: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const service = getAuthService();
      await service.removeUser();
      await service.signinRedirect(returnTo ? { state: { returnTo } } : undefined);
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Login failed:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    isLoading.value = true;
    try {
      const service = getAuthService();
      await service.signoutRedirect();
      user.value = null;
      useUserStore().clearUserContext();
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Logout failed:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Completes the OIDC redirect. Resolves to the `returnTo` path passed to
   * login(), or undefined when there was none. Not validated here: callers
   * must run it through safeReturnPath() before navigating.
   */
  async function handleCallback(): Promise<string | undefined> {
    isLoading.value = true;
    try {
      const service = getAuthService();
      const callbackUser = await service.signinCallback();
      user.value = callbackUser ?? (await service.getUser());
      if (user.value && !user.value.expired) {
        await loadRbac(user.value);
      }
      isInitialized.value = true;
      const state = callbackUser?.state as { returnTo?: unknown } | undefined;
      return typeof state?.returnTo === "string" ? state.returnTo : undefined;
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Callback failed:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function refreshToken() {
    try {
      const service = getAuthService();
      user.value = await service.signinSilent();
      console.log("[Auth] Token refreshed");
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Token refresh failed:", err);
      throw err;
    }
  }

  /**
   * Get access token for API calls
   * Used internally by axios interceptor
   */
  async function getAccessToken(): Promise<string | null> {
    // If no user, try to load from session
    if (!user.value) {
      await initialize();
    }

    // Check if token is expired
    if (user.value?.expired) {
      try {
        await refreshToken();
      } catch {
        return null;
      }
    }

    return accessToken.value;
  }

  return {
    // State
    user,
    isLoading,
    error,
    isInitialized,
    // Getters
    isAuthenticated,
    accessToken,
    userProfile,
    // RBAC
    ability,
    can,
    // Actions
    initialize,
    login,
    logout,
    handleCallback,
    refreshToken,
    getAccessToken,
  };
});
